import { Hex, Rgb } from "@open-kappa/colors"
import { createContext, useCallback, useEffect, useMemo, useState } from "react"
import { safeAccent, safeAccentLight } from "../lib/colorUtils"

type ColorContextValue = {
	color: Rgb
	hex: string
	setHex: (hex: string) => void
}

export const ColorContext = createContext<ColorContextValue>({
	color: Rgb.fromComponents([138, 66, 245, 1]),
	hex: "#8a42f5",
	setHex: () => {},
})

export function useColorProvider(initialHex = "#8a42f5") {
	const [hex, setHexRaw] = useState(initialHex)

	const color = useMemo(() => {
		try {
			return Hex.fromRgbHexString(hex).to(Rgb)
		} catch {
			return Rgb.fromComponents([138, 66, 245, 1])
		}
	}, [hex])

	useEffect(() => {
		const root = document.documentElement
		root.style.setProperty("--accent", safeAccent(color))
		root.style.setProperty("--accent-light", safeAccentLight(color))
	}, [color])

	// Require full 6-digit hex to avoid flicker from partial/shorthand input
	const setHex = useCallback((value: string) => {
		const cleaned = value.replace(/[^0-9a-fA-F#]/g, "")
		if (cleaned.length >= 7 && cleaned.startsWith("#")) {
			setHexRaw(cleaned.slice(0, 7))
		} else if (cleaned.length >= 6 && !cleaned.startsWith("#")) {
			setHexRaw(`#${cleaned.slice(0, 6)}`)
		}
	}, [])

	return useMemo<ColorContextValue>(
		() => ({ color, hex, setHex }),
		[color, hex, setHex],
	)
}
